/**
 * AdminBottomNav Component
 *
 * Bottom navigation for Platform Admin routes.
 * Issue #1063: Replace user navigation with admin-specific nav on admin routes
 *
 * Layout: Dashboard | Clubs | Invites | Reports
 * Visible on: /platform-admin/* and /admin/* routes
 */

import { useNavigate, useLocation } from "react-router-dom";
import { ADMIN_NAV_ITEMS } from '../config/navigationConfig';
import { getZIndexClass } from '../config/zIndex';
import type { LucideIcon } from 'lucide-react';

export function AdminBottomNav() {
  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 ${getZIndexClass('bottomNav')} rounded-t-2xl md:hidden`}
      style={{
        backgroundColor: 'var(--oro-surface-alt)',
        borderTop: '1px solid var(--oro-border)',
        boxShadow: 'var(--shadow-floating)',
        padding: '0.75rem 0',
        position: 'fixed',
        willChange: 'transform',
        paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0))',
      }}
    >
      {/* Admin mode indicator bar */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5"
        style={{ backgroundColor: 'var(--oro-warning)' }}
      />

      <div className="flex items-center justify-around max-w-md mx-auto">
        {ADMIN_NAV_ITEMS.map((item) => (
          <AdminNavItem
            key={item.id}
            path={item.path}
            label={item.label}
            icon={item.icon}
          />
        ))}
      </div>
    </nav>
  );
}

interface AdminNavItemProps {
  path: string;
  label: string;
  icon: LucideIcon;
}

function AdminNavItem({ path, label, icon: Icon }: AdminNavItemProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current path matches this nav item
  // For /platform-admin/clubs/:slug, we want "Clubs" to be active
  const isActive = location.pathname === path ||
    (path === '/platform-admin/clubs' && location.pathname.startsWith('/platform-admin/clubs'));

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Scroll to top if tapping current tab
    if (isActive) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(path);
  };

  return (
    <button
      onClick={handleClick}
      className="flex flex-col items-center justify-center gap-1 py-2 px-3 transition-colors"
      style={{
        color: isActive ? 'var(--oro-gold)' : 'var(--text-muted)'
      }}
      type="button"
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[11px] font-medium">
        {label}
      </span>
    </button>
  );
}
